import type { ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed border';

  const variants = {
    primary:
      'bg-[#1f2933] text-white border-transparent hover:bg-[#323f4b] dark:bg-[#e5e5e5] dark:text-[#111111] dark:hover:bg-[#d4d4d4]',
    secondary:
      'bg-white text-[#1f2933] border-gray-300 hover:bg-gray-50 dark:bg-[#1a1a1a] dark:text-[#e5e5e5] dark:border-[#333333] dark:hover:bg-[#222222]',
    ghost:
      'bg-transparent text-[#1f2933] border-transparent hover:bg-gray-100 dark:hover:bg-[#2a2a2a] dark:text-[#e5e5e5]',
    danger: 'bg-red-600 text-white border-transparent hover:bg-red-700',
  };

  const sizes = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 py-2 text-sm',
    lg: 'h-12 px-6 text-base',
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}
